import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Dialog } from '@progress/kendo-react-dialogs';
import I18n, { translate } from '@utils/i18n';
import api from '@utils/axios';
import toast from 'react-hot-toast';
import { createPaymentRecordPaymentsPath } from 'routes';

const namespace = 'features.invoices.index.table.actions.record_payment';

function RecordPaymentModal({ onClose, invoice, onDataChanged }) {
  const balance = Number(invoice.balanceRaw);

  const [loading, setLoading] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState(() => (balance > 0 ? balance.toFixed(2) : ''));
  const [paymentMethod, setPaymentMethod] = useState('');
  const [referenceNo, setReferenceNo] = useState('');
  const [paymentDate, setPaymentDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  const [notes, setNotes] = useState('');
  const [remainingAmount, setRemainingAmount] = useState(() => (balance > 0 ? 0 : balance));

  const currencySymbol = invoice.currencySymbol || '$';

  const paymentMethodOptions = [
    { value: 'Credit Card', label: translate('credit_card', { namespace }) },
    { value: 'ACH eCheck', label: translate('ach_echeck', { namespace }) },
    { value: 'Check', label: translate('check', { namespace }) },
    { value: 'Cash', label: translate('cash', { namespace }) }
  ];

  const handleAmountChange = useCallback(
    (e) => {
      let amount = parseFloat(e.target.value) || 0;

      if (amount > balance) {
        amount = balance;
        e.target.value = amount.toFixed(2);
      } else if (amount < 0) {
        amount = 0;
        e.target.value = '0.00';
      }

      setPaymentAmount(amount.toFixed(2));
      setRemainingAmount(balance - amount);
    },
    [balance]
  );

  const isFormValid = () => {
    return paymentAmount && parseFloat(paymentAmount) > 0 && paymentMethod && paymentDate;
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (!isFormValid()) return;

    setLoading(true);

    api
      .post(createPaymentRecordPaymentsPath({ format: 'json' }), {
        resource_type: invoice.resourceType || 'Invoice',
        resource_id: invoice.id,
        amount: paymentAmount,
        method: paymentMethod,
        ref: referenceNo,
        date: paymentDate,
        note: notes
      })
      .then(({ data }) => {
        toast.success(
          translate('payment_success', {
            namespace,
            amount: `${currencySymbol}${parseFloat(paymentAmount).toFixed(2)}`
          })
        );
        onClose();
        onDataChanged();
      })
      .catch(({ response }) => {
        const errorMessage =
          response?.data?.message || response?.responseText || translate('payment_failed', { namespace });
        toast.error(errorMessage);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  if (!invoice) return null;

  return (
    <Dialog width={500} title={translate('title', { namespace })} onClose={onClose} className="qmb-dialog">
      <div className="qmb-form--payment">
        <div className="modal__section">
          <h2>
            <sub>
              <i className="fa-solid fa-list" /> {translate('invoice_number', { namespace, number: invoice.invoiceNo })}
            </sub>
            <span className="--primary">{translate('amount_due', { namespace, amount: invoice.balance })}</span>
          </h2>
          <em className="--empty-msg">{translate('cc_warning', { namespace })}</em>
        </div>

        <form onSubmit={onSubmit} id="record-payment-form" disabled={loading}>
          <div className="modal__section">
            <div className="qmb-input--number--x-full">
              <input
                type="number"
                step="0.01"
                min="0.01"
                value={paymentAmount}
                onChange={handleAmountChange}
                placeholder=" "
                required
              />
              <label className="qmb-label">{translate('amount', { namespace, currency: currencySymbol })}</label>
              <em className="--empty-msg" style={{ justifySelf: 'right' }}>
                {translate('remaining', { namespace, amount: `${currencySymbol}${remainingAmount.toFixed(2)}` })}
              </em>
            </div>

            <div className="qmb-select --x-full">
              <select
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                placeholder=" "
                className="custom-field"
                required>
                <option value="" disabled hidden>
                  {' '}
                </option>
                {paymentMethodOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <label className="qmb-label">{translate('payment_method', { namespace })}</label>
            </div>

            <div className="qmb-input--text--x-full">
              <input type="text" value={referenceNo} onChange={(e) => setReferenceNo(e.target.value)} placeholder=" " />
              <label className="qmb-label">{translate('reference_no', { namespace })}</label>
            </div>

            <div className="qmb-input--text--x-full">
              <input
                type="date"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
                placeholder=" "
                required
              />
              <label className="qmb-label">{translate('date', { namespace })}</label>
            </div>

            <div className="qmb-textarea --x-full form__field">
              <textarea
                value={notes}
                onChange={(e) => {
                  if (e.target.value.length <= 255) {
                    setNotes(e.target.value);
                  }
                }}
                rows="4"
                cols="50"
                maxLength="255"
              />
              <label className="qmb-label">{translate('notes', { namespace })}</label>
              <span className="textarea__count">{255 - notes.length}</span>
            </div>
          </div>
        </form>

        <div
          className="modal__footer"
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {loading && (
            <div className="text-center" style={{ alignSelf: 'center', marginRight: 'auto' }}>
              <div className="load-spinner load-16">
                <svg role="img" title="loading">
                  <use href="/map.svg#load-spinner" />
                </svg>
                <span>{translate('processing', { namespace })}</span>
              </div>
            </div>
          )}
          <div style={{ marginLeft: 'auto' }}>
            <button type="button" className="qmb-button" onClick={onClose}>
              {I18n.t('generic.cancel')}
            </button>
            <button
              disabled={loading || !isFormValid()}
              className="qmb-button--submit"
              type="submit"
              form="record-payment-form">
              {I18n.t('generic.submit')}
            </button>
          </div>
        </div>
      </div>
    </Dialog>
  );
}

RecordPaymentModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onDataChanged: PropTypes.func.isRequired,
  invoice: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    invoiceNo: PropTypes.string.isRequired,
    balance: PropTypes.string.isRequired,
    balanceRaw: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    currencySymbol: PropTypes.string,
    resourceType: PropTypes.string
  }).isRequired
};

export default RecordPaymentModal;
